<template>
    <hox-modal :max-width-px="700" resizable @close="close">
        <template #header>Download</template>

        <DownloadPlatformSelect v-model="exportFormat" />

        <Tabs v-if="exportFormat" :model-value="tab" @onClick="onTabChange">
            <TabPane label="Download" name="downloadOptions">
                <DownloadDisclaimer :export-format="exportFormat" @tabChange="onTabChange" />
                <div class="download-modal__options-wrapper">
                    <DownloadHTMLOptions
                        v-if="isHtmlDownload || isImageDownload"
                        :export-format="exportFormat"
                        :max-image-scale="maxImageScale"
                    />

                    <DownloadVideoOptions
                        v-if="exportFormat === EXPORT_FORMAT.VIDEO && templateType === TemplateType.HTML5"
                    />

                    <DownloadAEVideoOptions
                        v-if="exportFormat === EXPORT_FORMAT.VIDEO && templateType === TemplateType.AE"
                    />

                    <DownloadExcelOptions v-if="exportFormat === EXPORT_FORMAT.FLASHTALKING_EXCEL" />
                </div>
            </TabPane>

            <TabPane label="Changes overview" name="changesOverview">
                <DownloadModalPlatformDetails :export-format="exportFormat" :template-type="templateType" />
            </TabPane>
        </Tabs>

        <template #footer>
            <Button class="download-modal__button" data-testid="asset-download__button--cancel" @click="close">
                Cancel
            </Button>

            <Button
                class="download-modal__button"
                type="primary"
                data-testid="asset-download__button--download"
                :disabled="buttonDisabled"
                @click="processDownloads"
            >
                Download
            </Button>
        </template>
    </hox-modal>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import { TemplateType } from "shared-utils/enums/masterTemplate";
import {
    EXPORT_FORMAT,
    EXPORT_CONFIGS,
    HTML_EXPORT_FORMATS,
    HTML_IMAGE_EXPORT_FORMATS,
    IMAGE_SCALES,
    type ExportFormatType,
    type ImageScale
} from "shared-utils/enums/exports";
import { getMaxImageScale } from "shared-utils/utils/imageScale";
import { USER_ACTIONS } from "shared-utils/enums/user";

import { NotificationTypes } from "@/enums/notifications";
import { JobStatusToNotificationStatus, JobNames } from "@/enums/jobs";
import { createAEJob } from "@/utils/ae";
import computeAfterEffectsLayers from "@/utils/computeAfterEffectsLayers";
import type { ConstructedDeliverable } from "@/types/types";
import { downloadJob } from "@/services/deliverables/publish";

import { JobsAction } from "@/store/modules/jobs";
import { NotificationsAction } from "@/store/modules/notifications";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";

import DownloadDisclaimer from "@/components/Download/DownloadDisclaimer.vue";
import DownloadHTMLOptions from "@/components/Download/DownloadHTMLOptions.vue";
import DownloadVideoOptions from "@/components/Download/DownloadVideoOptions.vue";
import DownloadExcelOptions from "@/components/Download/DownloadExcelOptions.vue";
import DownloadPlatformSelect from "@/components/Campaign/DownloadPlatformSelect.vue";
import DownloadModalPlatformDetails from "@/components/Download/DownloadModalPlatformDetails.vue";
import DownloadAEVideoOptions from "@/components/Download/DownloadAEVideoOptions.vue";

import { useDownload } from "@/composables/download/useDownload";
import { useParams } from "@/composables/useParams";
import { useDownloadAEVideo } from "@/composables/download/useDownloadAEVideo";
import { useGA4Tracking } from "@/composables/useGA4Tracking";
import GA4 from "@/enums/ga4Events";
import { userCan } from "@/services/Auth";

function transformPlatformOptsToFreeform(platformOpts: Record<string, unknown>) {
    return Object.entries(platformOpts).reduce<string[]>((acc, [key, value]) => {
        let stringValue: string;

        if (Array.isArray(value)) {
            stringValue = `${key}: [${value.join(", ")}]`;
        } else if (typeof value === "boolean") {
            if (!value) {
                return acc;
            }

            stringValue = `${key}`;
        } else if (typeof value === "string") {
            stringValue = `${key}: ${value}`;
        } else {
            stringValue = `${key}: ${value}`;
        }

        acc.push(stringValue);
        return acc;
    }, []);
}

export default defineComponent({
    components: {
        DownloadPlatformSelect,
        DownloadModalPlatformDetails,
        DownloadDisclaimer,
        DownloadHTMLOptions,
        DownloadVideoOptions,
        DownloadExcelOptions,
        DownloadAEVideoOptions
    },
    props: {
        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        deliverableIdentifiers: {
            type: Array as PropType<string[]>
        },
        downloadDeliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        isImportDownload: {
            required: false,
            type: Boolean
        }
    },

    emits: ["close"],

    setup() {
        const { clientId, campaignId } = useParams();
        const { computeQueryParams, getIsButtonDisabled, resetDownloadOptions } = useDownload();
        const { computeAEOutput } = useDownloadAEVideo();
        const ga4 = useGA4Tracking();

        return {
            clientId,
            campaignId,
            ga4,

            getIsButtonDisabled,

            computeQueryParams,
            resetDownloadOptions,
            computeAEOutput
        };
    },

    data() {
        return {
            exportFormat: "" as ExportFormatType | "",
            tab: "downloadOptions",
            EXPORT_FORMAT,
            TemplateType,
            isCompleteEventTracked: false
        };
    },
    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),

        userCanDownloadDeliverable() {
            return userCan(USER_ACTIONS.canDownloadDeliverable, {
                clientId: this.clientId,
                campaignId: this.campaignId
            });
        },

        isHtmlDownload() {
            return HTML_EXPORT_FORMATS.includes(this.exportFormat as (typeof HTML_EXPORT_FORMATS)[number]);
        },

        isImageDownload() {
            return HTML_IMAGE_EXPORT_FORMATS.includes(this.exportFormat as (typeof HTML_IMAGE_EXPORT_FORMATS)[number]);
        },

        /**
         * Template dimensions the scale selector has to respect. With library filters the
         * deliverable set is resolved server-side, so gate against every template in the campaign
         * rather than only the ones currently loaded into the modal.
         */
        scaleGatingTemplates() {
            const filters = this.creativeLibraryFiltersStore.getComputedLibraryFilters;

            if (filters && Object.keys(filters).length > 0) {
                return this.$store.state.campaign.masterTemplates;
            }

            return this.finalDeliverables.map(deliverable => deliverable.masterTemplate);
        },

        maxImageScale(): ImageScale {
            const largestOffered = IMAGE_SCALES[IMAGE_SCALES.length - 1];

            return this.scaleGatingTemplates.reduce<ImageScale>(
                (acc, masterTemplate) =>
                    Math.min(
                        acc,
                        getMaxImageScale(masterTemplate?.width ?? 0, masterTemplate?.height ?? 0)
                    ) as ImageScale,
                largestOffered
            );
        },

        templateType() {
            return this.$store.state.campaign.campaignType ?? TemplateType.HTML5;
        },

        buttonDisabled() {
            return this.getIsButtonDisabled(this.exportFormat);
        },

        platformOpts() {
            if (!this.exportFormat) {
                return {};
            }
            return this.computeQueryParams(this.exportFormat);
        },

        finalDeliverables() {
            if (this.isImportDownload) {
                return this.downloadDeliverables;
            }

            return this.deliverables;
        }
    },

    watch: {
        userCanDownloadDeliverable() {
            if (!this.userCanDownloadDeliverable) {
                this.$snackbar.warning("You do not have access to download deliverables");
                this.close();
            }
        },

        exportFormat(newVal: ExportFormatType | "", oldVal: ExportFormatType | "") {
            if (newVal && newVal !== oldVal) {
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_SETTINGS,
                    featureCategory: GA4.FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                    featureContext: `Download ${newVal ? EXPORT_CONFIGS[newVal].label : newVal}`
                });
            }

            this.resetDownloadOptions();
        }
    },

    mounted() {
        this.ga4.trackFeatureEvent({
            featureEvent: GA4.EVENT_TYPES.FEATURE_ACTIVATE,
            featureCategory: GA4.FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
            featureContext: GA4.FEATURE_CONTEXTS.CREATIVE_LIBRARY_DOWNLOAD
        });

        this.resetDownloadOptions();
    },

    methods: {
        close() {
            if (!this.isCompleteEventTracked) {
                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                    featureContext: GA4.FEATURE_CONTEXTS.CANCEL_DOWNLOAD
                });
            }
            this.$emit("close");
        },

        onTabChange(tab: "downloadOptions" | "changesOverview") {
            this.tab = tab;
        },

        async processDownloads() {
            if (this.exportFormat === "") {
                return;
            }

            if (this.exportFormat === EXPORT_FORMAT.VIDEO && this.templateType === TemplateType.AE) {
                try {
                    this.isCompleteEventTracked = true;
                    this.ga4.trackFeatureEvent({
                        featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                        featureCategory: GA4.FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                        featureContext: GA4.FEATURE_CONTEXTS.DOWNLOAD_SUCCESSFUL,
                        freeformValues: transformPlatformOptsToFreeform(this.platformOpts)
                    });

                    this.close();
                    const output = this.computeAEOutput();
                    const inputs = computeAfterEffectsLayers(this.finalDeliverables);
                    const job = await createAEJob({
                        inputs,
                        user: {
                            id: this.$store.state.auth.me?.id as string,
                            name: this.$store.state.auth.me?.role.name as string
                        },
                        campaignId: this.campaignId,
                        output
                    });

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        id: job.jobId,
                        message: "Your ads are being prepared for download",
                        status: JobStatusToNotificationStatus[job.status],
                        type: NotificationTypes.PlainlyBatchRender,
                        job: {
                            _id: job.jobId,
                            message: "Your ads are being prepared for download",
                            status: JobStatusToNotificationStatus[job.status],
                            type: NotificationTypes.PlainlyBatchRender
                        }
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job.jobId,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your ads are being prepared for download. Take a look at your notifications to check on progress"
                    );
                } catch (e) {
                    this.ga4.trackError({
                        errorMessage: (e as Error).message,
                        errorType: GA4.ERROR_TYPES.SYSTEM_ERROR,
                        origin: GA4.ERROR_ORIGINS.BACKEND,
                        featureName: GA4.FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD
                    });
                    this.$snackbar.error(
                        "There was an unexpected error while preparing your ads for download. Please try again in a few moments"
                    );
                }
            } else {
                this.isCompleteEventTracked = true;

                this.ga4.trackFeatureEvent({
                    featureEvent: GA4.EVENT_TYPES.FEATURE_COMPLETE,
                    featureCategory: GA4.FEATURE_CATEGORIES.CREATIVE_LIBRARY_DOWNLOAD,
                    featureContext: GA4.FEATURE_CONTEXTS.DOWNLOAD_SUCCESSFUL,
                    freeformValues: transformPlatformOptsToFreeform(this.platformOpts)
                });

                this.close();

                const jobNotificationMap = {
                    [JobNames.PUBFT_DOWNLOAD_EXCEL]: NotificationTypes.FlashtalkingDownloadExcel,
                    [JobNames.PUBFT_DOWNLOAD_ASSETS]: NotificationTypes.FlashtalkingDownloadAssets,
                    [JobNames.ADOBE_INDESIGN_DOWNLOAD]: NotificationTypes.AdobeIndesignDownload
                };

                try {
                    const job = await downloadJob({
                        campaignId: this.campaignId,
                        exportFormat: this.exportFormat,
                        deliverables: this.finalDeliverables,
                        deliverableIdentifiers: this.deliverableIdentifiers,
                        libraryFilters: this.creativeLibraryFiltersStore.getComputedLibraryFilters,
                        platformOpts: this.platformOpts
                    });

                    const notificationId = await this.$store.dispatch(NotificationsAction.Add, {
                        message: job.message,
                        status: JobStatusToNotificationStatus[job.status],
                        type:
                            jobNotificationMap[job.name as keyof typeof jobNotificationMap] ||
                            NotificationTypes.DownloadJob,
                        job
                    });

                    this.$store.dispatch(JobsAction.SetNotificationIdByActiveJobId, {
                        jobId: job._id,
                        notificationId
                    });

                    this.$snackbar.info(
                        "Your ads are being prepared for download. Take a look at your notifications to check on progress"
                    );
                } catch {
                    this.$snackbar.error(
                        "There was an unexpected error while preparing your ads for download. Please try again in a few moments"
                    );
                }
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.download-modal__button {
    margin: 0 0 0 $spacing-small;
}

.download-modal__options-wrapper {
    display: flex;
    flex-direction: column;
    gap: 7px;
}
</style>
